import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { useLang } from "../i18n";
import Seo from "../components/Seo";
import { Eyebrow, Reveal, GoldButton } from "../components/motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const HEADERS = {
  contact: { title: "inquiry.contactTitle", sub: "inquiry.contactSub", cta: "common.send" },
  corporate: { title: "inquiry.corporateTitle", sub: "inquiry.corporateSub", cta: "inquiry.corporateCta" },
  events: { title: "inquiry.eventsTitle", sub: "inquiry.eventsSub", cta: "inquiry.eventsCta" },
  bespoke: { title: "inquiry.bespokeTitle", sub: "inquiry.bespokeSub", cta: "inquiry.bespokeCta" },
};
const IMGS = {
  contact: "https://images.pexels.com/photos/4253315/pexels-photo-4253315.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  corporate: "https://images.pexels.com/photos/14646752/pexels-photo-14646752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  events: "https://images.pexels.com/photos/39405349/pexels-photo-39405349.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bespoke: "https://images.unsplash.com/photo-1600891964092-4316c288032e?w=1200&q=80",
};

export default function Inquiry({ type }) {
  const { t, lang } = useLang();
  const h = HEADERS[type];
  const extended = type !== "contact";
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", event_type: "", date: "", location: "", guests: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { type, language: lang, name: form.name, email: form.email, message: form.message };
      ["phone", "company", "event_type", "date", "location", "budget"].forEach((k) => form[k] && (payload[k] = form[k]));
      if (form.guests) payload.guests = Number(form.guests);
      await api.post("/inquiries", payload);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-[72px]">
      <Seo title={`${t(h.title)} — Chef à Domicile`} description={t(h.sub)} />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <Eyebrow>Chef à Domicile</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-5">{t(h.title)}</h1>
          <p className="text-neutral-400 leading-relaxed mb-10 max-w-lg">{t(h.sub)}</p>
          <img src={IMGS[type]} alt={t(h.title)} loading="lazy" className="w-full aspect-[4/3] object-cover hidden lg:block" />
        </Reveal>
        <Reveal delay={0.1}>
          {sent ? (
            <div className="bg-coal border border-gold/30 p-12 text-center" data-testid="inquiry-success">
              <CheckCircle2 size={40} className="text-gold mx-auto mb-5" />
              <p className="font-serif text-2xl text-ivory mb-3">{t("inquiry.success")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-coal border border-white/8 p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid={`inquiry-form-${type}`}>
              <div>
                <label className="lux-label">{t("auth.name")} *</label>
                <input data-testid="inquiry-name-input" required className="lux-input" value={form.name} onChange={upd("name")} />
              </div>
              <div>
                <label className="lux-label">{t("auth.email")} *</label>
                <input data-testid="inquiry-email-input" required type="email" className="lux-input" value={form.email} onChange={upd("email")} />
              </div>
              <div>
                <label className="lux-label">{t("inquiry.phone")}</label>
                <input data-testid="inquiry-phone-input" className="lux-input" value={form.phone} onChange={upd("phone")} />
              </div>
              {type === "corporate" && (
                <div>
                  <label className="lux-label">{t("inquiry.company")}</label>
                  <input data-testid="inquiry-company-input" className="lux-input" value={form.company} onChange={upd("company")} />
                </div>
              )}
              {extended && (
                <>
                  <div>
                    <label className="lux-label">{t("inquiry.eventType")}</label>
                    <input data-testid="inquiry-event-type-input" className="lux-input" value={form.event_type} onChange={upd("event_type")} />
                  </div>
                  <div>
                    <label className="lux-label">{t("inquiry.date")}</label>
                    <input data-testid="inquiry-date-input" type="date" className="lux-input" value={form.date} onChange={upd("date")} />
                  </div>
                  <div>
                    <label className="lux-label">{t("inquiry.location")}</label>
                    <input data-testid="inquiry-location-input" className="lux-input" value={form.location} onChange={upd("location")} />
                  </div>
                  <div>
                    <label className="lux-label">{t("inquiry.guests")}</label>
                    <input data-testid="inquiry-guests-input" type="number" min="1" className="lux-input" value={form.guests} onChange={upd("guests")} />
                  </div>
                  <div>
                    <label className="lux-label">{t("inquiry.budget")}</label>
                    <input data-testid="inquiry-budget-input" className="lux-input" placeholder="€" value={form.budget} onChange={upd("budget")} />
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <label className="lux-label">{t("inquiry.message")} *</label>
                <textarea data-testid="inquiry-message-input" required rows={5} className="lux-input" value={form.message} onChange={upd("message")} />
              </div>
              <div className="sm:col-span-2">
                <GoldButton type="submit" disabled={sending} data-testid="inquiry-submit-btn" className="w-full">
                  {sending ? t("common.sending") : t(h.cta)}
                </GoldButton>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
